import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

export interface CmpsFormField {
    id?: string;
    name: string;
    labelId?: string;
    label: string;
    placeholder?: string;
    validators?: any[];
    runtimeValidators?: any[];
    validatorDirectives?: any[];
    validationMsgs?: { [key: string]: any };
}

export interface CmpsFormFields<F extends CmpsFormField = CmpsFormField> {
    [key: string]: F;
}

export class CmpsForm<F extends CmpsFormFields = CmpsFormFields> {
    private readonly _fieldKeys: string[];
    readonly warnings: any;
    readonly errors: any;
    private readonly _sub: Subscription;
    submitted: boolean;

    constructor(
        private readonly _translateService: TranslateService,
        public readonly form: FormGroup,
        public readonly fields: F,
        public readonly name: string,
    ) {
        this._fieldKeys = Object.keys(this.fields);
        this._sub = this.form.statusChanges.subscribe(() =>
            this._setWarningsErrors(),
        );

        this.warnings = {};
        this.errors = {};
        this.clearWarningsErrors();
    }

    protected _renderMsg(
        field: CmpsFormField,
        msg: string,
        errors: any,
    ): string {
        if (!msg) return '';
        if (msg.length === 0) return msg;
        const fieldLabel =
            !field.label || field.label.length === 0
                ? ''
                : this._translateService.instant(field.label);
        msg = this._translateService.instant(msg);
        msg = msg.replace(/{{field_label}}/g, fieldLabel);
        if (typeof errors === 'object') {
            for (const key in errors) {
                let errorMsg = errors[key];
                if (!errorMsg) continue;
                if (typeof errorMsg !== 'string') {
                    try {
                        errorMsg = errorMsg.toString();
                    } catch (err) {
                        console.log("can't cast to string");
                        continue;
                    }
                }
                if (errorMsg.length === 0) continue;
                errorMsg = this._translateService.instant(errorMsg);
                msg = msg.replace(new RegExp(`{{${key}}}`, 'g'), errorMsg);
            }
        }
        return msg;
    }

    protected _mapMsgs(
        field: CmpsFormField,
        fieldKey: string,
        dest: any,
        src: any,
    ): void {
        for (const key in src) {
            const msg = !field.validationMsgs
                ? null
                : field.validationMsgs[key];
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            dest[fieldKey] += this._renderMsg(field, msg, src[key]) + ' ';
        }
        dest[fieldKey] = dest[fieldKey].trim();
    }

    protected _setWarnings(fieldKey: string): void {
        this.warnings[fieldKey] = '';
        const field = this.fields[fieldKey];
        if (!field.validatorDirectives) return;
        const control = this.form.get(field.name);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (control && control.valid) {
            const warnings = field.validatorDirectives
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, no-prototype-builtins
                .filter(
                    (value) =>
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        value &&
                        // eslint-disable-next-line no-prototype-builtins
                        value.hasOwnProperty('warnings') &&
                        value.warnings != null,
                )
                .map((value) => value.warnings)
                .reduce((a, b) => Object.assign(a, b), {});
            this._mapMsgs(field, fieldKey, this.warnings, warnings);
        }
    }

    protected _setErrors(fieldKey: string): void {
        this.errors[fieldKey] = '';
        const field = this.fields[fieldKey];
        const control = this.form.get(field.name);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (control && control.dirty && !control.valid) {
            this._mapMsgs(field, fieldKey, this.errors, control.errors);
        }
    }

    protected _setWarningsErrors(): void {
        if (!this.form) return;
        this._fieldKeys.forEach((key) => {
            this._setWarnings(key);
            this._setErrors(key);
        });
    }

    checkWarningsErrors(): void {
        Object.keys(this.form.controls).forEach((c) =>
            this.form.controls[c].markAsDirty(),
        );
        this._setWarningsErrors();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    clearWarningsErrors() {
        this._fieldKeys.forEach((key) => {
            this.warnings[key] = '';
            this.errors[key] = '';
        });
    }

    reset(): void {
        this.submitted = false;
        this.clearWarningsErrors();

        if (!this.form) return;
        this.form.reset();
    }

    destroy(): void {
        if (this._sub) {
            this._sub.unsubscribe();
        }
    }
}

@Injectable()
export class CmpsFormBuilder {
    constructor(
        private readonly _formBuilder: FormBuilder,
        private readonly _translateService: TranslateService,
    ) {}

    create<F extends CmpsFormFields = CmpsFormFields>(
        fields: F,
        name?: string,
    ): CmpsForm<F> {
        const form = this._formBuilder.group({});
        const keys = Object.keys(fields);
        keys.forEach((key) => {
            const field = fields[key];
            const c = this._formBuilder.control(null);
            form.addControl(field.name, c);
        });
        keys.forEach((key) => {
            const field = fields[key];
            const c = form.get(field.name);
            let validators = [];
            if (field.validators) {
                validators = validators.concat(field.validators);
            }
            if (field.runtimeValidators) {
                validators = validators.concat(
                    field.runtimeValidators.map((v) => v.fn(form, v.options)),
                );
            }
            c.setValidators(validators);
        });
        return new CmpsForm(
            this._translateService,
            form,
            fields,
            name || `form${Math.random()}`,
        );
    }
}

function findElement(
    name: string,
    fieldId: string,
    fieldName: string,
): HTMLElement {
    let el;
    if (fieldId) {
        el = document.querySelector(`form[name="${name}"] [id="${fieldId}"]`);
    }
    if (fieldName) {
        el = document.querySelector(
            `form[name="${name}"] [name="${fieldName}"]`,
        );
    }
    return el;
}

export function getElement<F extends CmpsFormFields = CmpsFormFields>(
    form: CmpsForm<F> | string,
    field: CmpsFormField | string,
): HTMLElement {
    const name = typeof form === 'string' ? form : form.name;
    let el;
    if (typeof field === 'string') {
        el = findElement(name, null, field);
    } else {
        if (field.id) {
            el = findElement(name, field.id, null);
        }
        if (!el) {
            el = findElement(name, null, field.name);
        }
    }
    if (!el) {
        console.error(`element not found`, form, field);
    }
    return el;
}
